import { useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface Article { readonly articleId: string; readonly articleCode: string; readonly approvalStatus: string; }

const CATEGORIES = ['SOP', 'POLICY', 'TRAINING', 'BEST_PRACTICE', 'FAQ', 'HOWTO', 'PROCESS', 'LESSON', 'KNOWLEDGE_ARTICLE'];

/** M27 — Knowledge Authoring Screen (§31). Drafts a new KB article and submits
 * it into the maker-checker workflow (BR-02). */
export function M27KnowledgeAuthoringPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('SOP');
  const [body, setBody] = useState('');
  const [created, setCreated] = useState<Article | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(submitForReview: boolean): Promise<void> {
    if (!title.trim() || !body.trim()) { setError(t('m27.knowledgeAuthoring.errRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: Article }>('/v1/collab/knowledge', { token, method: 'POST', body: { title, category, body } });
      let article = r.data;
      if (submitForReview) {
        const s = await apiRequest<{ data: Article }>(`/v1/collab/knowledge/${article.articleId}/submit`, { token, method: 'POST' });
        article = s.data;
      }
      setCreated(article); setError(null); setTitle(''); setBody('');
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <main aria-label="knowledge-authoring">
      <h1>{t('m27.knowledgeAuthoring.heading')}</h1>
      {error && <p role="alert">{error}</p>}
      {created && <p aria-label="created-article">{t('m27.knowledgeAuthoring.savedArticle', { code: created.articleCode, status: created.approvalStatus })}</p>}
      <label htmlFor="kb-title">{t('m27.knowledgeAuthoring.fldTitle')}</label>
      <input id="kb-title" value={title} onChange={(e) => setTitle(e.target.value)} />
      <label htmlFor="kb-category">{t('m27.knowledgeAuthoring.fldCategory')}</label>
      <select id="kb-category" value={category} onChange={(e) => setCategory(e.target.value)}>
        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <label htmlFor="kb-body">{t('m27.knowledgeAuthoring.fldBody')}</label>
      <textarea id="kb-body" value={body} onChange={(e) => setBody(e.target.value)} />
      <button type="button" disabled={busy} onClick={() => void save(false)}>{t('m27.knowledgeAuthoring.btnSaveDraft')}</button>
      <button type="button" disabled={busy} onClick={() => void save(true)}>{t('m27.knowledgeAuthoring.btnSubmitReview')}</button>
    </main>
  );
}
